export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: string | null;
  meta?: { total: number; page: number; limit: number };
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(path, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...init?.headers },
      ...init,
    });
    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }
    // The API always speaks the envelope; surface it directly.
    if (parsed && typeof parsed === "object" && "success" in parsed) {
      return parsed as ApiResponse<T>;
    }
    // Non-enveloped response (proxy/error page / empty body) → friendly message.
    return {
      success: false,
      data: null,
      error: res.ok ? "Unexpected response from the server." : `Request failed (${res.status}).`,
    };
  } catch {
    return {
      success: false,
      data: null,
      error: "Can't reach Ondetatu right now. Check your connection and try again.",
    };
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
};
